import { ChannelServiceType, ChannelType, IChannel, IChannelFilter, OrderTypes } from './IChannel';
import { DealerSettingName } from './IDealerSettings';

export interface IWorkspace {
  displayName: string;
  shortVersion: WorkspaceShorter;
  id: WorkspaceModule;
  ownerType?: 'NGX-MESSAGING.service_advisor' | 'NGX-MESSAGING.sales_agent';
  hasFiltersOpenByDefault: boolean;
  features: WorkspaceFeatures[];
  config?: IWorkspaceConfig;
  channelsFilters: IChannelFilter;
  maxCharactersOnMessage: number;
  defaultTypeService: ChannelServiceType;
  attachmentsType?: string;
  productType?: 'RO' | 'SO';
  tokenAuthentication?: 'x-authorization-truvideo' | 'x-authorization-messaging';
}

export type WebsockerParameters = 'subjectMessageableEntity' | 'subAccountMessageableEntity';

export type WorkspaceShorter = 'MA' | 'IC' | 'RE' | 'OC' | 'EX' | 'SC' | 'VA';

export type DinamicLinksURL = 'urlHyperlinkRepairOrder';

export type WorkspaceModule = 'sales' | 'service' | 'internal_chat' | 'marketing' | 'external' | 'reputation' | 'va';

export type RepairOrderUrl =
  | 'https://beta.truvideo.com/crud/repair-order/view/'
  | 'https://rc.truvideo.com/crud/repair-order/view/'
  | 'https://app.truvideo.com/crud/repair-order/view/'
  | 'https://uat.truvideo.com/crud/repair-order/view/';

export interface IFilters {
  type: FiltersTypes;
  comparationType?: 'equal' | 'mayor' | 'minor' | 'mayorequal' | 'minorequal' | 'array';
  comparationValue?: number | ChannelType;
  value: boolean | string | string[] | null;
  checkValue?: ChannelServiceType | boolean | 'ownerId' | ChannelType | 'UNREAD';
  isManualFilter?: boolean;
  name: string;
  field?: keyof IChannel | string;
  fieldLocalFilter?: keyof IChannel;
  disableOthers?: string[];
  isTagFilter?: boolean;
  isReverseFilter?: boolean;
  options?: { name: string; value: string | boolean }[];
  tooltip?: string;
  dealerSettings?: DealerSettingName[];
}

export interface IWorkspaceConfig {
  maxFiles?: number;
  dynamicLinks?: {
    pattern: RegExp;
    url: DinamicLinksURL | null;
    previewTemplate: string;
    previewLastMessage: string;
  }[];
}

export enum FiltersTypes {
  check = 'CHECK',
  selector = 'SELECTOR',
}

export enum WorkspaceFeatures {
  canAddNewMembers = 'canAddNewMembers',
  canCreateBroadcastChannel = 'canCreateBroadcastChannel',
  canCreateConversation = 'canCreateConversation',
  canCreateGrupalConversations = 'canCreateGrupalConversations',
  canDisplayInWorkspacesColumn = 'canDisplayInWorkspacesColumn',
  canDoScrollMessages = 'canDoScrollMessages',
  canFilterByTypeChannel = 'canFilterByTypeChannel',
  canGetChannelInfo = 'canGetChannelInfo',
  canMakeVideoCalls = 'canMakeVideoCalls',
  canMuteChannels = 'canMuteChannels',
  canPinChannels = 'canPinChannels',
  canProfessionalizeMessages = 'canProfessionalizeMessages',
  canSeeConversationInfo = 'canSeeConversationInfo',
  canChangeConversationStatus = 'canChangeConversationStatus',
  canSeeHyperlinks = 'canSeeHyperlinks',
  canSwitchDealers = 'canSwitchDealers',
  canTranslateMessages = 'canTranslateMessages',
  canUseAutoResponses = 'canUseAutoResponses',
  canUseFilters = 'canUseFilters',
  canUseSearchFilter = 'canUseSearchFilter',
  canUseSmartResponses = 'canUseSmartResponses',
  validateOptInOptOut = 'validateOptInOptOut',
  canUseOldEndpoints = 'canUseOldEndpoints',
  showSettingsButton = 'showSettingsButton',
  showPhoneInChannelList = 'showPhoneInChannelList',
  showStatusInChannelList = 'showStatusInChannelList',
  showOwnerOnChannelList = 'showOwnerOnChannelList',
  showOwnerOnChatHeader = 'showOwnerOnChatHeader',
  setChannelOwnerByUserId = 'setChannelOwnerByUserId',
  setChannelOwnerByTag = 'setChannelOwnerByTag',
  showOwnerMessages = 'showOwnerMessages',
  showOrderInChannelList = 'showOrderInChannelList',
  showChannelPhoneNumberTypeIconOnChannelHeader = 'showChannelPhoneNumberTypeIconOnChannelHeader',
  showChannelPhoneNumberTypeIconOnChannelsList = 'showChannelPhoneNumberTypeIconOnChannelsList',
  canUseOptInToggle = 'canUseOptInToggle',
  canTryCreateChannelByPhone = 'canTryCreateChannelByPhone',
  canTryCreateChannelBySearchTerm = 'canTryCreateChannelBySearchTerm',
  showStatusBanner = 'showStatusBanner',
  showMembersCount = 'showMembersCount',
  canUseMesagesTemplates = 'canUseMesagesTemplates',
  needCheckEnableTextConversations = 'needCheckEnableTextConversations',
  useDealerOutcoming = 'useDealerOutcoming',
  canUseMemberStatus = 'canUseMemberStatus',
  canSeeChatMembersPhotos = 'canSeeChatMembersPhotos',
  startConversationInfoOpen = 'startConversationInfoOpen',
  canEditDisplayNameChannel = 'canEditDisplayNameChannel',
  assignOrphanDisplayName = 'assignOrphanDisplayName',
  canUseReactions = 'canUseReactions',
  canUseWhatsapp = 'canUseWhatsapp',
  showUnreadsCounter = 'showUnreadsCounter',
  canUseDealerSettings = 'canUseDealerSettings',
  showUnreadsInChannelList = 'showUnreadsInChannelList',
  canUseChannelUIDonURL = 'canUseChannelUIDonURL',
  useRoutingUrl = 'useRoutingUrl',
  canUseLastMessagesBanner = 'canUseLastMessagesBanner',
  canUseOldMessagesBanner = 'canUseOldMessagesBanner',
  setMessageOwnerByTag = 'setMessageOwnerByTag',
  setMessageOwnerByCreatedBy = 'setMessageOwnerByCreatedBy',
  setMessageOwnerByUserId = 'setMessageOwnerByUserId',
  setDealerWebsocket = 'setDealerWebsocket',
  setUserWebsocket = 'setUserWebsocket',
  setChannelPhoneNumberFromTo = 'setChannelPhoneNumberFromTo',
  canUseBotMode = 'canUseBotMode',
  checkInputPhoneNumber = 'checkInputPhoneNumber',
  canChangePhoneNumber = 'canChangePhoneNumber',
  canChangeOwner = 'canChangeOwner',
  canReadAllMessages = 'canReadAllMessages',
  autoMarkAsRead = 'autoMarkAsRead',
}

export const WORKSPACES: IWorkspace[] = [
  {
    defaultTypeService: 'SMS',
    displayName: 'Virtual assistant',
    shortVersion: 'VA',
    id: 'va',
    hasFiltersOpenByDefault: true,
    maxCharactersOnMessage: 1200,
    features: [
      WorkspaceFeatures.canGetChannelInfo,
      WorkspaceFeatures.canProfessionalizeMessages,
      WorkspaceFeatures.canTranslateMessages,
      WorkspaceFeatures.canUseAutoResponses,
      WorkspaceFeatures.canUseSmartResponses,
      WorkspaceFeatures.canSeeConversationInfo,
      WorkspaceFeatures.canUseOldEndpoints,
      WorkspaceFeatures.canDoScrollMessages,
      WorkspaceFeatures.canUseSearchFilter,
      WorkspaceFeatures.validateOptInOptOut,
      WorkspaceFeatures.canUseFilters,
      WorkspaceFeatures.showPhoneInChannelList,
      WorkspaceFeatures.showOwnerOnChannelList,
      WorkspaceFeatures.showOwnerOnChatHeader,
      WorkspaceFeatures.setChannelOwnerByUserId,
      WorkspaceFeatures.showChannelPhoneNumberTypeIconOnChannelHeader,
      WorkspaceFeatures.showChannelPhoneNumberTypeIconOnChannelsList,
      WorkspaceFeatures.showOrderInChannelList,
      WorkspaceFeatures.canTryCreateChannelByPhone,
      WorkspaceFeatures.canUseMesagesTemplates,
      WorkspaceFeatures.useDealerOutcoming,
      WorkspaceFeatures.startConversationInfoOpen,
      WorkspaceFeatures.canEditDisplayNameChannel,
      WorkspaceFeatures.assignOrphanDisplayName,
      WorkspaceFeatures.canUseWhatsapp,
      WorkspaceFeatures.canUseDealerSettings,
      WorkspaceFeatures.useRoutingUrl,
      WorkspaceFeatures.canUseLastMessagesBanner,
      WorkspaceFeatures.canUseOldMessagesBanner,
      WorkspaceFeatures.showUnreadsInChannelList,
      WorkspaceFeatures.setMessageOwnerByUserId,
      WorkspaceFeatures.showOwnerMessages,
      WorkspaceFeatures.setDealerWebsocket,
      WorkspaceFeatures.setChannelPhoneNumberFromTo,
      WorkspaceFeatures.checkInputPhoneNumber,
      WorkspaceFeatures.canUseBotMode,
    ],
    channelsFilters: {
      searchTerm: '',
      filters: [],
      page: 1,
      size: 20,
      orderType: OrderTypes.SALES_ORDER,
    },
  },
  {
    defaultTypeService: 'SMS',
    displayName: 'External API',
    shortVersion: 'EX',
    id: 'external',
    hasFiltersOpenByDefault: false,
    maxCharactersOnMessage: 160,
    tokenAuthentication: 'x-authorization-messaging',
    features: [
      WorkspaceFeatures.canGetChannelInfo,
      WorkspaceFeatures.useRoutingUrl,
      WorkspaceFeatures.canUseLastMessagesBanner,
      WorkspaceFeatures.canUseOldMessagesBanner,
      WorkspaceFeatures.canTryCreateChannelByPhone,
      WorkspaceFeatures.showOwnerOnChannelList,
      WorkspaceFeatures.showOwnerOnChatHeader,
      WorkspaceFeatures.setChannelOwnerByTag,
      WorkspaceFeatures.setMessageOwnerByTag,
      WorkspaceFeatures.showChannelPhoneNumberTypeIconOnChannelHeader,
      WorkspaceFeatures.showOwnerMessages,
      WorkspaceFeatures.setDealerWebsocket,
      WorkspaceFeatures.canChangePhoneNumber,
      WorkspaceFeatures.canDoScrollMessages,
      WorkspaceFeatures.canSeeConversationInfo,
      WorkspaceFeatures.autoMarkAsRead,
    ],
    channelsFilters: {
      searchTerm: '',
      filters: [],
      page: 1,
      size: 20,
    },
  },
  {
    defaultTypeService: 'SMS',
    displayName: 'Sales conversation API',
    shortVersion: 'SC',
    id: 'sales',
    productType: 'SO',
    ownerType: 'NGX-MESSAGING.sales_agent',
    hasFiltersOpenByDefault: true,
    maxCharactersOnMessage: 1200,
    features: [
      WorkspaceFeatures.canGetChannelInfo,
      WorkspaceFeatures.canProfessionalizeMessages,
      WorkspaceFeatures.canTranslateMessages,
      WorkspaceFeatures.canUseAutoResponses,
      WorkspaceFeatures.canUseSmartResponses,
      WorkspaceFeatures.canSeeConversationInfo,
      WorkspaceFeatures.canDisplayInWorkspacesColumn,
      WorkspaceFeatures.canUseOldEndpoints,
      WorkspaceFeatures.canDoScrollMessages,
      WorkspaceFeatures.canUseSearchFilter,
      WorkspaceFeatures.validateOptInOptOut,
      WorkspaceFeatures.canUseFilters,
      WorkspaceFeatures.showPhoneInChannelList,
      WorkspaceFeatures.showOwnerOnChannelList,
      WorkspaceFeatures.showOwnerOnChatHeader,
      WorkspaceFeatures.setChannelOwnerByUserId,
      WorkspaceFeatures.showChannelPhoneNumberTypeIconOnChannelHeader,
      WorkspaceFeatures.showChannelPhoneNumberTypeIconOnChannelsList,
      WorkspaceFeatures.showOrderInChannelList,
      WorkspaceFeatures.canTryCreateChannelByPhone,
      WorkspaceFeatures.canUseMesagesTemplates,
      WorkspaceFeatures.needCheckEnableTextConversations,
      WorkspaceFeatures.canPinChannels,
      WorkspaceFeatures.useDealerOutcoming,
      WorkspaceFeatures.startConversationInfoOpen,
      WorkspaceFeatures.canEditDisplayNameChannel,
      WorkspaceFeatures.assignOrphanDisplayName,
      WorkspaceFeatures.canUseWhatsapp,
      WorkspaceFeatures.canUseDealerSettings,
      WorkspaceFeatures.useRoutingUrl,
      WorkspaceFeatures.canUseLastMessagesBanner,
      WorkspaceFeatures.canUseOldMessagesBanner,
      WorkspaceFeatures.showUnreadsInChannelList,
      WorkspaceFeatures.setMessageOwnerByUserId,
      WorkspaceFeatures.showOwnerMessages,
      WorkspaceFeatures.setDealerWebsocket,
      WorkspaceFeatures.setChannelPhoneNumberFromTo,
      WorkspaceFeatures.checkInputPhoneNumber,
      WorkspaceFeatures.canChangeOwner,
      WorkspaceFeatures.canReadAllMessages,
    ],
    channelsFilters: {
      searchTerm: '',
      filters: [
        {
          type: FiltersTypes.check,
          value: true,
          name: 'Owns',
          checkValue: 'ownerId',
          field: 'ownerId',
          comparationType: 'equal',
          isTagFilter: true,
        },
        {
          type: FiltersTypes.check,
          value: 'WHATSAPP',
          checkValue: 'WHATSAPP',
          comparationType: 'equal',
          name: 'Whatsapp',
          field: 'routing',
          dealerSettings: [DealerSettingName.WhatsAppSalesModule, DealerSettingName.WhatsAppServiceModule],
        },
        {
          type: FiltersTypes.check,
          value: null,
          comparationType: 'equal',
          checkValue: 'SMS',
          name: 'SMS',
          field: 'routing',
          dealerSettings: [DealerSettingName.WhatsAppSalesModule, DealerSettingName.WhatsAppServiceModule],
        },
        {
          type: FiltersTypes.check,
          value: false,
          name: 'unreadMessages',
          comparationValue: 0,
          comparationType: 'mayor',
          field: 'readStatus',
          fieldLocalFilter: 'unreadMessages',
          checkValue: 'UNREAD',
        },
        {
          type: FiltersTypes.check,
          value: false,
          name: 'initiated',
          field: 'initiated',
          checkValue: true,
        },
      ],
      page: 1,
      size: 20,
      orderType: OrderTypes.SALES_ORDER,
    },
  },
  {
    defaultTypeService: 'SMS',
    displayName: 'Orders Conversation API',
    shortVersion: 'OC',
    ownerType: 'NGX-MESSAGING.service_advisor',
    productType: 'RO',
    hasFiltersOpenByDefault: true,
    id: 'service',
    maxCharactersOnMessage: 1200,
    features: [
      WorkspaceFeatures.canGetChannelInfo,
      WorkspaceFeatures.canProfessionalizeMessages,
      WorkspaceFeatures.canTranslateMessages,
      WorkspaceFeatures.canUseAutoResponses,
      WorkspaceFeatures.canUseSmartResponses,
      WorkspaceFeatures.canSeeConversationInfo,
      WorkspaceFeatures.canDisplayInWorkspacesColumn,
      WorkspaceFeatures.canUseOldEndpoints,
      WorkspaceFeatures.canDoScrollMessages,
      WorkspaceFeatures.canUseSearchFilter,
      WorkspaceFeatures.validateOptInOptOut,
      WorkspaceFeatures.canUseFilters,
      WorkspaceFeatures.showPhoneInChannelList,
      WorkspaceFeatures.showOrderInChannelList,
      WorkspaceFeatures.showOwnerOnChannelList,
      WorkspaceFeatures.showOwnerOnChatHeader,
      WorkspaceFeatures.setChannelOwnerByUserId,
      WorkspaceFeatures.canTryCreateChannelByPhone,
      WorkspaceFeatures.showChannelPhoneNumberTypeIconOnChannelHeader,
      WorkspaceFeatures.showChannelPhoneNumberTypeIconOnChannelsList,
      WorkspaceFeatures.canUseMesagesTemplates,
      WorkspaceFeatures.needCheckEnableTextConversations,
      WorkspaceFeatures.canPinChannels,
      WorkspaceFeatures.useDealerOutcoming,
      WorkspaceFeatures.startConversationInfoOpen,
      WorkspaceFeatures.canEditDisplayNameChannel,
      WorkspaceFeatures.assignOrphanDisplayName,
      WorkspaceFeatures.canUseWhatsapp,
      WorkspaceFeatures.canUseDealerSettings,
      WorkspaceFeatures.useRoutingUrl,
      WorkspaceFeatures.canUseLastMessagesBanner,
      WorkspaceFeatures.canUseOldMessagesBanner,
      WorkspaceFeatures.showUnreadsInChannelList,
      WorkspaceFeatures.setMessageOwnerByUserId,
      WorkspaceFeatures.showOwnerMessages,
      WorkspaceFeatures.setDealerWebsocket,
      WorkspaceFeatures.setChannelPhoneNumberFromTo,
      WorkspaceFeatures.canUseBotMode,
      WorkspaceFeatures.checkInputPhoneNumber,
      WorkspaceFeatures.canChangeOwner,
      WorkspaceFeatures.canReadAllMessages,
    ],
    channelsFilters: {
      searchTerm: '',
      filters: [
        {
          type: FiltersTypes.check,
          value: true,
          isManualFilter: false,
          name: 'Owns',
          checkValue: 'ownerId',
          comparationType: 'equal',
          field: 'ownerId',
          isTagFilter: true,
        },
        {
          type: FiltersTypes.check,
          value: 'WHATSAPP',
          checkValue: 'WHATSAPP',
          comparationType: 'equal',
          name: 'Whatsapp',
          field: 'routing',
          dealerSettings: [DealerSettingName.WhatsAppSalesModule, DealerSettingName.WhatsAppServiceModule],
        },
        {
          type: FiltersTypes.check,
          value: null,
          checkValue: 'SMS',
          comparationType: 'equal',
          name: 'SMS',
          field: 'routing',
          dealerSettings: [DealerSettingName.WhatsAppSalesModule, DealerSettingName.WhatsAppServiceModule],
        },
        {
          type: FiltersTypes.check,
          value: false,
          name: 'unreadMessages',
          comparationValue: 0,
          comparationType: 'mayor',
          field: 'readStatus',
          fieldLocalFilter: 'unreadMessages',
          checkValue: 'UNREAD',
        },
        {
          type: FiltersTypes.check,
          value: false,
          name: 'initiated',
          field: 'initiated',
          checkValue: true,
        },
      ],
      page: 1,
      size: 20,
      orderType: OrderTypes.REPAIR_ORDER,
    },
  },
  {
    displayName: 'Marketing',
    shortVersion: 'MA',
    id: 'marketing',
    hasFiltersOpenByDefault: true,
    maxCharactersOnMessage: 1200,
    defaultTypeService: 'SMS',
    features: [
      WorkspaceFeatures.canDisplayInWorkspacesColumn,
      WorkspaceFeatures.canGetChannelInfo,
      WorkspaceFeatures.canProfessionalizeMessages,
      WorkspaceFeatures.canSeeConversationInfo,
      WorkspaceFeatures.canUseFilters,
      WorkspaceFeatures.canUseSearchFilter,
      WorkspaceFeatures.canUseSmartResponses,
      WorkspaceFeatures.validateOptInOptOut,
      WorkspaceFeatures.canTranslateMessages,
      WorkspaceFeatures.canUseOptInToggle,
      WorkspaceFeatures.useDealerOutcoming,
      WorkspaceFeatures.canUseDealerSettings,
      WorkspaceFeatures.showUnreadsInChannelList,
      WorkspaceFeatures.canUseChannelUIDonURL,
      WorkspaceFeatures.setDealerWebsocket,
    ],
    channelsFilters: {
      searchTerm: '',
      page: 1,
      size: 20,
      filters: [
        {
          type: FiltersTypes.check,
          value: true,
          field: 'enabled',
          name: 'active',
          tooltip: 'hideConversationWhereUserHasOptedOut',
        },
        {
          type: FiltersTypes.check,
          value: true,
          field: 'onlyReplied',
          name: 'replies',
          tooltip: 'showConversationsWhereTheCustomerHasReplied',
        },
      ],
    },
  },
  {
    displayName: 'Reputation',
    shortVersion: 'RE',
    id: 'reputation',
    hasFiltersOpenByDefault: false,
    maxCharactersOnMessage: 500,
    defaultTypeService: 'SMS',
    features: [
      WorkspaceFeatures.canDisplayInWorkspacesColumn,
      WorkspaceFeatures.canGetChannelInfo,
      WorkspaceFeatures.canProfessionalizeMessages,
      WorkspaceFeatures.canUseSmartResponses,
      WorkspaceFeatures.canUseDealerSettings,
      WorkspaceFeatures.canUseChannelUIDonURL,
      WorkspaceFeatures.setDealerWebsocket,
    ],
    channelsFilters: {
      searchTerm: '',
      filters: [],
      page: 1,
      size: 20,
    },
  },
  {
    displayName: 'Internal chat',
    shortVersion: 'IC',
    id: 'internal_chat',
    hasFiltersOpenByDefault: false,
    maxCharactersOnMessage: 999,
    features: [
      WorkspaceFeatures.canAddNewMembers,
      WorkspaceFeatures.canCreateBroadcastChannel,
      WorkspaceFeatures.canCreateConversation,
      WorkspaceFeatures.canCreateGrupalConversations,
      WorkspaceFeatures.canDisplayInWorkspacesColumn,
      WorkspaceFeatures.canDoScrollMessages,
      WorkspaceFeatures.canFilterByTypeChannel,
      WorkspaceFeatures.canGetChannelInfo,
      WorkspaceFeatures.canMakeVideoCalls,
      WorkspaceFeatures.canSeeConversationInfo,
      WorkspaceFeatures.canSeeHyperlinks,
      WorkspaceFeatures.canUseFilters,
      WorkspaceFeatures.canUseSearchFilter,
      WorkspaceFeatures.canMuteChannels,
      WorkspaceFeatures.canPinChannels,
      WorkspaceFeatures.showSettingsButton,
      WorkspaceFeatures.showMembersCount,
      WorkspaceFeatures.canSeeChatMembersPhotos,
      WorkspaceFeatures.showUnreadsCounter,
      WorkspaceFeatures.canUseDealerSettings,
      WorkspaceFeatures.showUnreadsInChannelList,
      WorkspaceFeatures.canUseChannelUIDonURL,
      WorkspaceFeatures.setUserWebsocket,
      WorkspaceFeatures.setMessageOwnerByCreatedBy,
      WorkspaceFeatures.canUseReactions,
    ],
    config: {
      maxFiles: 1,
      dynamicLinks: [
        {
          pattern: /#+([a-zA-Z0-9_]+)/i,
          url: 'urlHyperlinkRepairOrder',
          previewTemplate: 'repairOrdersPreviewComponent',
          previewLastMessage: 'Repair order',
        },
        {
          pattern: /vc=+([a-zA-Z0-9\-_]+)/i,
          url: null,
          previewTemplate: 'videoCallModal',
          previewLastMessage: 'Video call',
        },
      ],
    },
    channelsFilters: {
      searchTerm: '',
      page: 1,
      size: 20,
      filters: [
        {
          type: FiltersTypes.check,
          value: false,
          comparationType: 'array',
          checkValue: ChannelType.GROUP,
          name: 'groups',
          field: 'channelTypes',
          fieldLocalFilter: 'type',
        },
        {
          type: FiltersTypes.check,
          value: false,
          comparationType: 'array',
          checkValue: ChannelType.BROADCAST,
          name: 'announcements',
          field: 'channelTypes',
          fieldLocalFilter: 'type',
        },
        {
          type: FiltersTypes.check,
          value: false,
          comparationType: 'array',
          checkValue: ChannelType.DIRECT_MESSAGE,
          name: 'direct_messages',
          field: 'channelTypes',
          fieldLocalFilter: 'type',
        },
        {
          type: FiltersTypes.check,
          value: false,
          isManualFilter: false,
          comparationType: 'equal',
          field: 'isPrivate',
          name: 'isPrivate',
        },
        {
          type: FiltersTypes.check,
          value: false,
          name: 'unreadMessages',
          comparationValue: 0,
          comparationType: 'mayor',
          field: 'readStatus',
          checkValue: 'UNREAD',
        },
        {
          type: FiltersTypes.check,
          value: false,
          name: 'initiated',
          field: 'initiated',
          checkValue: true,
        },
      ],
    },
    defaultTypeService: 'INTERNAL',
  },
];
